import { useRef, useState } from 'react';
import { Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover';
import { useNotificationRealtime } from '../hooks/useNotificationRealtime';
import {
	isNotificationsDisabled,
	useUnreadCount,
} from '../hooks/useNotifications';
import { NotificationPanel } from './NotificationPanel';

const BADGE_MAX = 99;

/**
 * Header bell: unread badge, the notification list in a popover, and the
 * realtime connection that keeps both fresh. Hidden when the backend has
 * notifications turned off.
 */
export function NotificationBell() {
	const unread = useUnreadCount();
	const [open, setOpen] = useState(false);
	const openRef = useRef(open);
	openRef.current = open;

	const disabled = isNotificationsDisabled(unread.error);
	useNotificationRealtime({
		enabled: !disabled,
		isListOpen: () => openRef.current,
	});

	if (disabled) return null;

	const count = unread.data ?? 0;
	const badge = count > BADGE_MAX ? `${BADGE_MAX}+` : String(count);
	const label = count > 0 ? `Notifications, ${count} unread` : 'Notifications';

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					type="button"
					variant="ghost"
					size="icon"
					className="relative"
					aria-label={label}
				>
					<Bell />
					{count > 0 ? (
						<span
							aria-hidden="true"
							className="bg-destructive absolute -top-0.5 -right-0.5 grid h-4 min-w-4 place-items-center rounded-full px-1 text-[10px] leading-none font-semibold text-white tabular-nums"
						>
							{badge}
						</span>
					) : null}
				</Button>
			</PopoverTrigger>
			<PopoverContent
				align="end"
				className="w-[min(24rem,calc(100vw-2rem))] p-0"
			>
				<NotificationPanel unreadCount={count} onClose={() => setOpen(false)} />
			</PopoverContent>
		</Popover>
	);
}
